import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Brand from "@/components/Brand";
import UtrSearch from "@/components/UtrSearch";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import BalanceNotification from "@/components/BalanceNotification";
import NotificationsBell from "@/components/NotificationsBell";
import OnlineUsers from "@/components/OnlineUsers";
import InstallPrompt from "@/components/InstallPrompt";
import QuickActionsFab from "@/components/QuickActionsFab";
import IosInstallHint from "@/components/IosInstallHint";
import UpdateBanner from "@/components/UpdateBanner";
import BroadcastGate from "@/components/BroadcastGate";
import ShortcutHint from "@/components/ShortcutHint";
import ShortcutHelpFab from "@/components/ShortcutHelpFab";
import InlineShortcutAssigner from "@/components/InlineShortcutAssigner";
import useGlobalHotkeys from "@/hooks/useGlobalHotkeys";
import { withBackParam } from "@/lib/backUrl";
import PushHealthBanner from "@/components/PushHealthBanner";
import BackButton from "@/components/BackButton";
import SuperUserBottomNav from "@/components/SuperUserBottomNav";
import {
    ChartLineUp, UsersThree, Tag, Receipt, UploadSimple, XCircle,
    ChartBar, Pulse, SignOut, List as MenuIcon, Key, Clock, Database,
    CaretDown, CaretRight, ShieldCheck, PaperPlaneTilt, UserPlus, Bell, Gear, CloudArrowUp,
    ClockCounterClockwise, FileText, ChartPie, QrCode, CalendarBlank, MagicWand, Warning, Trophy, Broom, Table as TableIcon,
    CurrencyInr, Queue, FileArrowUp, Buildings, Bank, BookOpen, CreditCard, EnvelopeSimple, ChatText, MagnifyingGlass,
    Wallet, Receipt as ReceiptIcon, Keyboard, Swap, ArrowsMerge, Scales,
} from "@phosphor-icons/react";

// ────────────────────────────────────────────────────────────────
// Task 3 — Top navigation is strictly 3 tabs with dropdown menus.
// v1.24.10 — Distinct colour per pill for better scannability:
//   Dashboard (indigo) · Administration (violet) · Transactions (teal)
//   Reports (amber) · Record Cash (emerald) · Record Payment (rose) · Upload (orange)
// ────────────────────────────────────────────────────────────────
// v1.27.91 — items with `hideForDEO: true` are hidden when the signed-in
// staff sub-user is a Data Entry Operator (staff_permission == "data_entry_operator").
// The whole Administration tab is hidden below via `hideEntireTabForDEO`.
const NAV = [
    {
        // v1.28.79 — Administration now carries ALL config screens again,
        // organised into logical section groups (Party Mgmt / Communication /
        // Settings / System). The separate "Settings" tab was removed per user
        // request — Administration is the single home for all admin actions.
        key: "administration",
        label: "Administration",
        icon: Gear,
        tone: "violet",   // v1.24.10
        items: [
            // ── PARTY MANAGEMENT ─────────────────────────────────────────
            { to: "/admin/parties?add=1", label: "Add Party", icon: UserPlus, testId: "menu-add-party", group: "party" },
            { to: "/admin/parties", label: "Manage Parties", icon: UsersThree, testId: "menu-manage-parties", group: "party" },
            { to: "/admin/parties", label: "Party Master", icon: UsersThree, testId: "menu-party-master", group: "party" },
            { to: "/admin/parties?resend=1", label: "Send Portal Link Again", icon: PaperPlaneTilt, testId: "menu-resend-portal", group: "party" },
            { to: "/admin/parties/merge", label: "Merge Parties", icon: ArrowsMerge, testId: "menu-merge-parties", group: "party" },
            { to: "/admin/parties/transfer-balance", label: "Transfer Balance", icon: Swap, testId: "menu-transfer-balance", group: "party" },
            { to: "/admin/parties/split", label: "Split Party Period", icon: ArrowsMerge, testId: "menu-split-party", group: "party" },

            // ── COMMUNICATION ────────────────────────────────────────────
            { to: "/admin/broadcast", label: "Broadcast Message", icon: Bell, testId: "menu-broadcast", hideForDEO: true, group: "communication" },

            // ── SETTINGS ─────────────────────────────────────────────────
            { to: "/admin/settings/payment-modes", label: "Payment Modes", icon: CreditCard, testId: "menu-payment-modes-admin", hideForDEO: true, group: "settings" },
            { to: "/admin/pricing", label: "Pricing Rules", icon: Tag, testId: "menu-pricing", hideForDEO: true, group: "settings" },
            { to: "/admin/mediators", label: "Mediators", icon: CreditCard, testId: "menu-mediators", hideForDEO: true, group: "settings" },
            { to: "/admin/portals", label: "Portals", icon: CreditCard, testId: "menu-portals", hideForDEO: true, group: "settings" },
            { to: "/admin/settings/remark-presets", label: "Remark Presets", icon: ChatText, testId: "menu-remark-presets", hideForDEO: true, group: "settings" },
            { to: "/admin/company-upis", label: "Company UPI IDs", icon: QrCode, testId: "menu-company-upis", hideForDEO: true, group: "settings" },
            { to: "/admin/blocked-upis", label: "Blocked UPI List", icon: ShieldCheck, testId: "menu-blocked-upis", hideForDEO: true, group: "settings" },
            { to: "/admin/upi-mappings", label: "UPI Account Mapping", icon: Bank, testId: "menu-upi-mappings", hideForDEO: true, group: "settings" },
            { to: "/admin/settings/shortcuts", label: "Keyboard Shortcuts", icon: Keyboard, testId: "menu-shortcuts", hideForDEO: true, group: "settings" },
            { to: "/admin/settings/role-permissions", label: "Role Permissions", icon: ShieldCheck, testId: "menu-role-permissions", superUserOnly: true, group: "settings" },

            // ── SYSTEM ───────────────────────────────────────────────────
            { to: "/admin/users", label: "Users (Staff)", icon: UsersThree, testId: "menu-users", companyAdminOnly: true, hideForDEO: true, group: "system" },
            { to: "/admin/settings/admins", label: "Admin Accounts", icon: ShieldCheck, testId: "menu-admins", superAdminOnly: true, group: "system" },
            { to: "/admin/login-history", label: "Login History", icon: ClockCounterClockwise, testId: "menu-login-history-admin", group: "system" },
            { to: "/admin/activity", label: "Activity Log", icon: Pulse, testId: "menu-activity-admin", group: "system" },
            { to: "/admin/release-notes", label: "Release Notes", icon: Bell, testId: "menu-release-notes", group: "system" },
            { to: "/admin/backups", label: "Automatic Backups", icon: ShieldCheck, testId: "menu-backups", hideForDEO: true, group: "system" },
            { to: "/admin/migration", label: "Data Migration", icon: Database, testId: "menu-data-migration", hideForDEO: true, group: "system" },
            { to: "/admin/super/companies", label: "Manage Companies", icon: Buildings, testId: "menu-super-companies", superUserOnly: true, group: "system" },
            { to: "/admin/super/backup-center", label: "Backup Center", icon: ShieldCheck, testId: "menu-super-backup-center", superUserOnly: true, group: "system" },
            { to: "/admin/super/daily-archive", label: "Daily G-Drive Archive", icon: CloudArrowUp, testId: "menu-super-daily-archive", superUserOnly: true, group: "system" },
            { to: "/admin/super/transfer-party", label: "Transfer Party", icon: Swap, testId: "menu-super-transfer-party", superUserOnly: true, group: "system" },
            { to: "/admin/super/trace-utr", label: "Trace UTR", icon: MagnifyingGlass, testId: "menu-super-trace-utr", superUserOnly: true, group: "system" },
            /* v1.28.65 — Cash Register + Cash Expenses temporarily hidden per user request.
               Un-comment the two entries below to re-enable. */
            // { to: "/admin/cash-register", label: "Cash Register", icon: Wallet, testId: "menu-cash-register", group: "system" },
            // { to: "/admin/cash-expenses", label: "Cash Expenses", icon: ReceiptIcon, testId: "menu-cash-expenses", group: "system" },
        ],
    },
    {
        key: "transactions",
        label: "Transactions",
        icon: Receipt,
        tone: "teal",     // v1.24.10
        items: [
            { to: "/admin/transactions", label: "Party Ledger", icon: Receipt, testId: "menu-party-ledger" },
            { to: "/admin/rejected", label: "Failed Slips", icon: XCircle, testId: "menu-failed-slips" },
            { to: "/admin/uploads?status=rejected", label: "Rejected Slips", icon: XCircle, testId: "menu-rejected-slips" },
            { to: "/admin/uploads?status=pending", label: "Pending Payment Slips", icon: UploadSimple, testId: "menu-pending-slips" },
            { to: "/admin/ocr-recheck", label: "OCR Re-check", icon: MagicWand, testId: "menu-ocr-recheck" },
        ],
    },
    {
        key: "reports",
        label: "Reports",
        icon: ChartBar,
        tone: "amber",    // v1.24.10
        // v1.28.48 — Report items now carry `group` so the dropdown can render
        // them under collapsible category headers (Party / Payment / Admin /
        // Other). Also drives the search + Recently Used sections.
        items: [
            { to: "/admin/reports", label: "Account Statement", icon: FileText, testId: "menu-account-statement", group: "party", shortcutAction: "open-ledger" },
            { to: "/admin/reports?segment=inactive", label: "Inactive Parties", icon: UsersThree, testId: "menu-inactive-parties", group: "party" },
            { to: "/admin/reports/top-parties", label: "Top Parties of the Month", icon: Trophy, testId: "menu-top-parties", group: "party" },
            { to: "/admin/reports/leaderboard", label: "Highest Earning Parties", icon: Trophy, testId: "menu-leaderboard", group: "party" },
            { to: "/admin/reports/reconcile-grid", label: "Reconcile Grid", icon: TableIcon, testId: "menu-reconcile-grid", group: "party" },
            { to: "/admin/reports/outstanding", label: "Outstanding Report", icon: ChartBar, testId: "menu-outstanding-report", group: "party" },
            { to: "/admin/reports/opening-closing", label: "Opening & Closing Balance", icon: Scales, testId: "menu-opening-closing", group: "party" },

            /* v1.28.65 — Cash Register report link temporarily hidden per user request. */
            // { to: "/admin/reports/cash-register", label: "Cash Register", icon: Wallet, testId: "menu-cash-register-report", group: "payment" },
            { to: "/admin/reports/day-book", label: "Day Book", icon: BookOpen, testId: "menu-day-book", group: "payment" },
            { to: "/admin/monthly-summary", label: "Monthly Summary", icon: CalendarBlank, testId: "menu-monthly-summary", group: "payment" },
            { to: "/admin/aging", label: "Aging Report", icon: Clock, testId: "menu-aging", group: "payment" },
            { to: "/admin/payments-by-upi", label: "Payments by UPI", icon: ChartBar, testId: "menu-payments-by-upi", group: "payment" },
            { to: "/admin/reports/commission", label: "Commission Report", icon: ChartLineUp, testId: "menu-commission", group: "payment" },
            { to: "/admin/reports/payment-modes", label: "Payment Modes Report", icon: ChartBar, testId: "menu-payment-modes", group: "payment" },
            { to: "/admin/reports/payment-calendar", label: "Payment Calendar", icon: CalendarBlank, testId: "menu-payment-calendar", group: "payment" },
            { to: "/admin/reports/verification-queue", label: "Verification Queue", icon: Queue, testId: "menu-verification-queue", group: "payment" },
            { to: "/admin/reports/suspense", label: "Suspense Entries", icon: Warning, testId: "menu-suspense", showSuspenseChip: true, group: "payment" },
            { to: "/admin/reports/stranded", label: "Stranded Commissions", icon: Broom, testId: "menu-stranded", group: "payment" },
            { to: "/admin/reports/duplicate-utrs", label: "Duplicate UTRs", icon: Warning, testId: "menu-duplicate-utrs", group: "payment" },
            { to: "/admin/reports/invalid-utrs", label: "Invalid UTR Records", icon: Warning, testId: "menu-invalid-utrs", group: "payment" },
            { to: "/admin/reports/mediator-commission", label: "Mediator Commission", icon: CreditCard, testId: "menu-mediator-commission", group: "payment" },

            { to: "/admin/activity", label: "Activity Report", icon: Pulse, testId: "menu-activity", group: "admin" },
            { to: "/admin/reports/user-activity", label: "User Activity Log", icon: Pulse, testId: "menu-user-activity", group: "admin" },
            { to: "/admin/login-history", label: "Login History", icon: ClockCounterClockwise, testId: "menu-login-history", group: "admin" },
            { to: "/admin/notifications", label: "Notifications", icon: Bell, testId: "menu-notifications", group: "admin" },
            { to: "/admin/email-log", label: "Email Log", icon: EnvelopeSimple, testId: "menu-email-log", group: "admin" },
        ],
    },
];

// v1.28.48 — Reports dropdown group definitions (order + labels).
const REPORT_GROUPS = [
    { key: "party",   label: "Party Reports" },
    { key: "payment", label: "Payment Reports" },
    { key: "admin",   label: "Admin Reports" },
    { key: "other",   label: "Other Reports" },
];

// v1.28.79 — Administration dropdown section headers, in display order.
const ADMIN_GROUPS = [
    { key: "party",         label: "Party Management" },
    { key: "communication", label: "Communication" },
    { key: "settings",      label: "Settings" },
    { key: "system",        label: "System" },
];

const RECENT_REPORTS_KEY = "micm_recent_reports";
const RECENT_REPORTS_MAX = 3;

function readRecentReports() {
    try {
        const raw = localStorage.getItem(RECENT_REPORTS_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, RECENT_REPORTS_MAX) : [];
    } catch { return []; }
}
function pushRecentReport(testId) {
    try {
        const list = readRecentReports().filter((v) => v !== testId);
        list.unshift(testId);
        localStorage.setItem(RECENT_REPORTS_KEY, JSON.stringify(list.slice(0, RECENT_REPORTS_MAX)));
    } catch { /* ignore */ }
}

// Per-pill colour palettes. Keys map to `tone` on each NAV / ACTION_PILL entry.
// Bold white text + a distinct saturated background per tab so admins can spot
// the right action at a glance (was: everything blue and monotonous).
const TONE = {
    indigo:  { base: "bg-indigo-600  hover:bg-indigo-700",  open: "bg-indigo-700  shadow-sm" },
    violet:  { base: "bg-violet-600  hover:bg-violet-700",  open: "bg-violet-700  shadow-sm" },
    teal:    { base: "bg-teal-600    hover:bg-teal-700",    open: "bg-teal-700    shadow-sm" },
    amber:   { base: "bg-amber-500   hover:bg-amber-600",   open: "bg-amber-600   shadow-sm" },
    emerald: { base: "bg-emerald-600 hover:bg-emerald-700", open: "bg-emerald-700 shadow-sm" },
    rose:    { base: "bg-rose-600    hover:bg-rose-700",    open: "bg-rose-700    shadow-sm" },
    orange:  { base: "bg-orange-500  hover:bg-orange-600",  open: "bg-orange-600  shadow-sm" },
};

// Direct-action pills (no dropdown) — shortened labels so they fit on one line
// alongside the dropdown tabs.
const ACTION_PILLS = [
    { to: "/admin?open=cash",   label: "Cash / Value Recd",       icon: CurrencyInr,    testId: "nav-record-cash",    isDeepLink: true,  tone: "emerald", shortcutAction: "record-payment" },
    { to: "/admin?open=payout", label: "Amt. Paid / Value Out",   icon: PaperPlaneTilt, testId: "nav-record-payment", isDeepLink: true,  tone: "rose",    shortcutAction: "record-payout" },
    { to: "/admin/uploads?upload=1&status=pending", label: "Upload Slips", icon: UploadSimple, testId: "nav-upload-slips", isDeepLink: false, tone: "orange"  },
    { to: "/admin/reports/verification-queue?upload=1", label: "Bank Data",  icon: FileArrowUp,   testId: "nav-upload-bank",   isDeepLink: false, tone: "indigo" },
];

// Direct-navigation pill for the top nav (used by both Dashboard and action pills)
function ActionPill({ to, label, icon: Icon, testId, isDeepLink, tone = "indigo", shortcutAction }) {
    const nav = useNavigate();
    const loc = useLocation();
    const t = TONE[tone] || TONE.indigo;
    const active = loc.pathname + loc.search === to;
    return (
        <span className="relative inline-flex items-center">
            <button
                type="button"
                data-testid={testId}
                onClick={() => {
                    // v1.29.17 — Deep-link pills MUST attach ?back=<current-url>
                    // so the Cash / Payout save handler on Dashboard can hop
                    // the admin back to the exact ledger they came from. Was:
                    // `nav(to)` with no back param — Dashboard's returnToSourceIfAny
                    // saw an empty backUrlRef and silently left the admin
                    // stranded on the main dashboard after saving. Now mirrors
                    // QuickActionsFab so the two entry points behave identically.
                    if (isDeepLink) nav(withBackParam(to), { replace: false });
                    else nav(to);
                }}
                className={`flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold text-white transition-colors duration-150 ${active ? t.open : t.base}`}
            >
                <Icon size={13} weight="bold" />
                <span>{label}</span>
                {shortcutAction && <ShortcutHint action={shortcutAction} />}
                {!shortcutAction && <ShortcutHint action={`nav:${testId}`} />}
            </button>
            <InlineShortcutAssigner
                action_id={shortcutAction || `nav:${testId}`}
                label={label}
                target_route={to}
                category="Action"
            />
        </span>
    );
}

const NAV_LINE = null; // placeholder, kept for legacy reference

// Desktop nav tab with dropdown menu
function NavTab({ tab, onSelect }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const location = useLocation();

    useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const Icon = tab.icon;
    const t = TONE[tab.tone] || TONE.indigo;

    // v1.28.48 — Enhanced Reports dropdown: search, Recently Used, collapsible
    // groups and compact spacing. Other tabs keep the existing simple menu.
    const isReports = tab.key === "reports";
    const visibleItems = tab.items.filter((it) => (!it.superAdminOnly || tab.isSuperAdmin) && (!it.superUserOnly || tab.isSuperUser) && !(tab.isDEO && it.hideForDEO));
    const [reportQuery, setReportQuery] = useState("");
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
    const [recentIds, setRecentIds] = useState(readRecentReports);
    useEffect(() => {
        if (open && isReports) setRecentIds(readRecentReports());
    }, [open, isReports]);
    const trackClick = (item) => {
        if (isReports) pushRecentReport(item.testId);
        setOpen(false);
        onSelect && onSelect();
    };
    const toggleGroup = (key) => setCollapsedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const filteredItems = isReports && reportQuery.trim()
        ? visibleItems.filter((it) => it.label.toLowerCase().includes(reportQuery.trim().toLowerCase()))
        : visibleItems;
    const groupedItems = isReports
        ? REPORT_GROUPS.map((g) => ({ ...g, items: filteredItems.filter((it) => (it.group || "other") === g.key) })).filter((g) => g.items.length > 0)
        : null;
    const recentItems = isReports
        ? recentIds.map((id) => visibleItems.find((it) => it.testId === id)).filter(Boolean)
        : [];

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                data-testid={`nav-tab-${tab.key}`}
                onClick={() => setOpen((v) => !v)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold text-white transition-colors duration-150 ${open ? t.open : t.base}`}
            >
                <Icon size={15} weight="bold" />
                <span>{tab.label}</span>
                <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && !isReports && (
                <div
                    data-testid={`nav-menu-${tab.key}`}
                    className={`absolute left-0 top-full z-40 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ${tab.key === "administration" ? "w-80 max-h-[75vh] overflow-y-auto" : "w-64"}`}
                >
                    <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {tab.label}
                    </div>
                    {tab.key === "administration" ? (
                        // v1.28.79 — Render Administration with section headers.
                        ADMIN_GROUPS.map((g) => {
                            const gItems = visibleItems.filter((it) => (it.group || "system") === g.key);
                            if (gItems.length === 0) return null;
                            return (
                                <div key={g.key} data-testid={`nav-admin-group-${g.key}`}>
                                    <div className="mt-1 border-t border-slate-100 bg-slate-50/60 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-violet-700 first:border-t-0 first:mt-0">
                                        {g.label}
                                    </div>
                                    <ul className="py-0.5">
                                        {gItems.map((item) => {
                                            const ItemIcon = item.icon;
                                            return (
                                                <li key={item.testId} className="flex items-center pr-1">
                                                    <NavLink
                                                        to={item.to}
                                                        data-testid={item.testId}
                                                        onClick={() => trackClick(item)}
                                                        className={({ isActive }) =>
                                                            `flex flex-1 items-center gap-2.5 px-3 py-1.5 text-sm transition ${
                                                                isActive
                                                                    ? "bg-violet-50 font-semibold text-violet-700"
                                                                    : "text-slate-700 hover:bg-slate-50"
                                                            }`
                                                        }
                                                    >
                                                        <ItemIcon size={14} className="text-slate-500" />
                                                        <span className="flex-1 truncate">{item.label}</span>
                                                        <ShortcutHint action={item.shortcutAction || `nav:${item.testId}`} />
                                                    </NavLink>
                                                    <InlineShortcutAssigner action_id={item.shortcutAction || `nav:${item.testId}`} label={item.label} target_route={item.to} category={tab.label} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })
                    ) : (
                        <ul className="py-1">
                            {visibleItems.map((item) => {
                                const ItemIcon = item.icon;
                                return (
                                    <li key={item.testId} className="flex items-center pr-1">
                                        <NavLink
                                            to={item.to}
                                            data-testid={item.testId}
                                            onClick={() => trackClick(item)}
                                            className={({ isActive }) =>
                                                `flex flex-1 items-center gap-2.5 px-3 py-2 text-sm transition ${
                                                    isActive
                                                        ? "bg-blue-50 font-semibold text-blue-700"
                                                        : "text-slate-700 hover:bg-slate-50"
                                                }`
                                            }
                                        >
                                            <ItemIcon size={15} className="text-slate-500" />
                                            <span className="flex-1">{item.label}</span>
                                            <ShortcutHint action={item.shortcutAction || `nav:${item.testId}`} />
                                            {item.showSuspenseChip && tab.suspenseCount > 0 && (
                                                <span
                                                    data-testid="nav-suspense-chip"
                                                    className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                                                >
                                                    {tab.suspenseCount}
                                                </span>
                                            )}
                                        </NavLink>
                                        <InlineShortcutAssigner action_id={item.shortcutAction || `nav:${item.testId}`} label={item.label} target_route={item.to} category={tab.label} />
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
            {open && isReports && (
                <div
                    data-testid={`nav-menu-${tab.key}`}
                    className="absolute left-0 top-full z-40 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
                >
                    {/* Search bar */}
                    <div className="border-b border-slate-100 bg-slate-50 p-2">
                        <div className="relative">
                            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                autoFocus
                                data-testid="reports-search-input"
                                placeholder="Search reports…"
                                value={reportQuery}
                                onChange={(e) => setReportQuery(e.target.value)}
                                className="w-full rounded-md border border-slate-200 bg-white py-1 pl-6 pr-2 text-xs text-slate-800 placeholder-slate-400 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                            />
                        </div>
                    </div>
                    <div className="max-h-[70vh] overflow-y-auto py-0.5">
                        {/* Recently Used */}
                        {!reportQuery.trim() && recentItems.length > 0 && (
                            <div data-testid="reports-recent-section">
                                <div className="px-3 pt-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                                    Recently Used
                                </div>
                                <ul>
                                    {recentItems.map((item) => {
                                        const ItemIcon = item.icon;
                                        // v1.29.18 — Recently Used rows now render the assigned
                                        // shortcut badge + `+` assigner just like the grouped
                                        // rows below. Was: this section rendered only label +
                                        // icon, so Ctrl+L (bound to Account Statement via the
                                        // `open-ledger` shortcutAction) was invisible here. Uses
                                        // the same action_id resolution as the grouped list —
                                        // `item.shortcutAction || nav:${item.testId}` — so the
                                        // two badges always agree.
                                        const shortcutId = item.shortcutAction || `nav:${item.testId}`;
                                        return (
                                            <li key={`recent-${item.testId}`} className="flex items-center pr-1">
                                                <NavLink
                                                    to={item.to}
                                                    data-testid={`recent-${item.testId}`}
                                                    onClick={() => trackClick(item)}
                                                    className={({ isActive }) =>
                                                        `flex flex-1 items-center gap-2 px-3 py-1 text-[13px] transition ${
                                                            isActive ? "bg-amber-50 font-semibold text-amber-800" : "text-slate-700 hover:bg-slate-50"
                                                        }`
                                                    }
                                                >
                                                    <ItemIcon size={13} className="text-slate-500 shrink-0" />
                                                    <span className="flex-1 truncate">{item.label}</span>
                                                    <ShortcutHint action={shortcutId} className="shrink-0" />
                                                </NavLink>
                                                <InlineShortcutAssigner action_id={shortcutId} label={item.label} target_route={item.to} category="Reports" />
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="mx-3 my-1 border-t border-slate-100" />
                            </div>
                        )}
                        {/* Grouped items */}
                        {groupedItems.length === 0 && (
                            <div className="px-3 py-6 text-center text-xs text-slate-400">No reports match "{reportQuery}"</div>
                        )}
                        {groupedItems.map((g) => {
                            const collapsed = collapsedGroups.has(g.key);
                            return (
                                <div key={g.key} data-testid={`reports-group-${g.key}`}>
                                    <button
                                        type="button"
                                        data-testid={`reports-group-toggle-${g.key}`}
                                        onClick={() => toggleGroup(g.key)}
                                        className="flex w-full items-center gap-1 px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500 hover:text-slate-800"
                                    >
                                        <CaretRight size={9} weight="bold" className={`transition-transform ${collapsed ? "" : "rotate-90"}`} />
                                        <span className="flex-1 text-left">{g.label}</span>
                                        <span className="text-[9px] text-slate-400">{g.items.length}</span>
                                    </button>
                                    {!collapsed && (
                                        <ul>
                                            {g.items.map((item) => {
                                                const ItemIcon = item.icon;
                                                // v1.29.18 — Reports grouped list must also honour
                                                // `item.shortcutAction` first (e.g. `open-ledger`
                                                // for Account Statement), otherwise the badge only
                                                // shows for items that don't declare a custom
                                                // shortcutAction. Mirrors the Administration /
                                                // Transactions tabs at lines 340 + 380.
                                                const shortcutId = item.shortcutAction || `nav:${item.testId}`;
                                                return (
                                                    <li key={item.testId} className="flex items-center pr-1">
                                                        <NavLink
                                                            to={item.to}
                                                            data-testid={item.testId}
                                                            onClick={() => trackClick(item)}
                                                            className={({ isActive }) =>
                                                                `flex flex-1 items-center gap-2 px-3 py-1 text-[13px] transition ${
                                                                    isActive ? "bg-blue-50 font-semibold text-blue-700" : "text-slate-700 hover:bg-slate-50"
                                                                }`
                                                            }
                                                        >
                                                            <ItemIcon size={13} className="text-slate-500 shrink-0" />
                                                            <span className="flex-1 truncate">{item.label}</span>
                                                            <ShortcutHint action={shortcutId} className="shrink-0" />
                                                            {item.showSuspenseChip && tab.suspenseCount > 0 && (
                                                                <span
                                                                    data-testid="nav-suspense-chip"
                                                                    className="inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-full bg-rose-600 px-1 py-0.5 text-[9px] font-bold text-white"
                                                                >
                                                                    {tab.suspenseCount}
                                                                </span>
                                                            )}
                                                        </NavLink>
                                                        <InlineShortcutAssigner action_id={shortcutId} label={item.label} target_route={item.to} category="Reports" />
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function DashboardPill() {
    return (
        <NavLink
            to="/admin"
            end
            data-testid="nav-dashboard"
            className={({ isActive }) =>
                `flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold text-white transition-colors duration-150 ${
                    isActive ? TONE.indigo.open : TONE.indigo.base
                }`
            }
        >
            <ChartLineUp size={13} weight="bold" />
            <span>Dashboard</span>
            <ShortcutHint action="go-dashboard" />
        </NavLink>
    );
}

// Vertical nav (used inside the mobile Sheet drawer) — dropdowns become sections
function MobileNav({ user, onLogout, onNavigate, onChangePassword, isSuperAdmin, isSuperUser, isDEO, companyName }) {
    return (
        <div className="flex h-full flex-col bg-slate-900 text-slate-100">
            <div className="border-b border-slate-800 px-5 py-5">
                <Link to="/admin" className="flex items-center gap-3" data-testid="brand-logo-mobile" onClick={onNavigate}>
                    <img src="/logo512.png" alt={companyName || "MICM"} className="logo-spin-glow h-14 w-14 rounded-lg object-contain bg-black" data-testid="brand-logo-img-mobile" />
                    <Brand
                        size="md"
                        shimmer
                        sub={companyName && companyName.trim().toLowerCase() !== "md impex india" ? companyName : "Admin Console"}
                        subClassName="text-slate-300"
                    />
                </Link>
            </div>
            <nav className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
                <NavLink
                    to="/admin"
                    end
                    data-testid="nav-dashboard-mobile"
                    onClick={onNavigate}
                    className={({ isActive }) =>
                        `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                            isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                        }`
                    }
                >
                    <ChartLineUp size={18} />
                    <span>Dashboard</span>
                </NavLink>
                {NAV.filter((tab) => !(isDEO && tab.hideEntireTabForDEO)).map((tab) => (
                    <div key={tab.key} className="space-y-0.5">
                        <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {tab.label}
                        </div>
                        {tab.items.filter((it) => (!it.superAdminOnly || isSuperAdmin) && (!it.superUserOnly || isSuperUser) && !(isDEO && it.hideForDEO)).map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.testId}
                                    to={item.to}
                                    data-testid={`${item.testId}-mobile`}
                                    onClick={onNavigate}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                                            isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                                        }`
                                    }
                                >
                                    <Icon size={17} />
                                    <span>{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                ))}
                {/* v1.24.10 — Direct action shortcuts in the mobile drawer (per-pill colour) */}
                <div className="space-y-0.5">
                    <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Quick Actions
                    </div>
                    {ACTION_PILLS.map((p) => {
                        const Icon = p.icon;
                        const t = TONE[p.tone] || TONE.indigo;
                        return (
                            <NavLink
                                key={p.testId}
                                to={p.to}
                                data-testid={`${p.testId}-mobile`}
                                onClick={onNavigate}
                                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-bold text-white ${t.base}`}
                            >
                                <Icon size={17} weight="bold" />
                                <span>{p.label}</span>
                            </NavLink>
                        );
                    })}
                </div>
            </nav>
            <div className="border-t border-slate-800 p-4">
                <div className="mb-3 text-xs text-slate-400">
                    <div className="font-medium text-slate-200">{user?.name}</div>
                    <div className="truncate">{user?.email}</div>
                </div>
                <button
                    onClick={onChangePassword}
                    className="mb-2 flex w-full items-center justify-center gap-2 rounded-md border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 hover:text-white"
                >
                    <Key size={16} />
                    <span>Change password</span>
                </button>
                <button
                    onClick={onLogout}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 hover:text-white"
                >
                    <SignOut size={16} />
                    <span>Sign out</span>
                </button>
            </div>
        </div>
    );
}

// Desktop user avatar / account dropdown
function UserMenu({ user, onLogout, onChangePassword }) {
    const [open, setOpen] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [currentCompany, setCurrentCompany] = useState(null);
    const [imp, setImp] = useState(() => localStorage.getItem("micm_impersonate_company") || "");
    const isSU = !!user?.is_super_user;
    const navigate = useNavigate();
    useEffect(() => {
        api.get("/me/company").then((r) => setCurrentCompany(r.data)).catch(() => {});
    }, [imp]);
    // v1.29.07 — Listen for the `micm-impersonation-changed` event so a
    // company switch triggered from ANOTHER component (SuperDashboard's
    // "Enter Company" chip, TransferParty's post-move redirect, any
    // future call site) refreshes the header pill and re-fetches the
    // "You are managing X" banner without a full page reload. Also
    // resynchronises `imp` from localStorage since the dispatcher wrote
    // there before firing.
    useEffect(() => {
        const onImp = (e) => {
            const next = e?.detail?.company_id ?? (localStorage.getItem("micm_impersonate_company") || "");
            setImp(next || "");
            api.get("/me/company").then((r) => setCurrentCompany(r.data)).catch(() => {});
        };
        window.addEventListener("micm-impersonation-changed", onImp);
        return () => window.removeEventListener("micm-impersonation-changed", onImp);
    }, []);
    useEffect(() => {
        if (!isSU || !open) return;
        api.get("/super/companies").then((r) => setCompanies(r.data.companies || [])).catch(() => {});
    }, [isSU, open]);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (!e.target.closest("[data-user-menu]")) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const pickCompany = (cid) => {
        if (cid) localStorage.setItem("micm_impersonate_company", cid);
        else localStorage.removeItem("micm_impersonate_company");
        setImp(cid || "");
        setOpen(false);
        // v1.28.96 — Was `window.location.reload()`, which triggered a full
        // app refresh, invalidated in-flight requests (each aborted → 401),
        // and the 401 interceptor (api.js) wiped `micm_impersonate_company`
        // as collateral. Net effect: SU switched company → app briefly went
        // to /admin/super/dashboard with no tenant context. Switching to
        // a React Router in-app nav keeps the impersonation cookie/header
        // in place and lets React re-scope every fetch on the natural
        // re-render — no reload, no dropped context.
        window.dispatchEvent(new CustomEvent("micm-impersonation-changed", { detail: { company_id: cid || null } }));
        navigate("/admin", { replace: true });
    };
    const initials = (user?.name || user?.email || "A").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div className="relative" data-user-menu>
            <button
                type="button"
                data-testid="user-menu-btn"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-1.5 py-1 pr-2.5 text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-slate-900 text-[11px] font-bold text-white">
                    {initials}
                </span>
                <span className="hidden text-xs font-medium sm:block">{currentCompany?.name || user?.name || "Admin"}</span>
                {/* v1.25.6 — Role badge (Super User / Company Admin / Company User) */}
                {isSU && !localStorage.getItem("micm_impersonate_company") ? (
                    <span data-testid="role-badge" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                        <ShieldCheck size={10} weight="fill" /> Super User
                    </span>
                ) : (
                    <span data-testid="role-badge" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                        <ShieldCheck size={10} weight="fill" />
                        {user?.role === "customer" ? "Company User, " : "Company Admin, "}
                        {currentCompany?.name || "—"}
                    </span>
                )}
                <CaretDown size={12} weight="bold" className="text-slate-400" />
            </button>
            {open && (
                <div className="absolute right-0 top-full z-30 mt-2 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                    <div className="border-b border-slate-100 px-3 py-2.5">
                        <div className="text-xs font-semibold text-slate-900">{user?.name}</div>
                        <div className="truncate text-[10px] text-slate-500">{user?.email}</div>
                        {currentCompany?.name && (
                            <div className="mt-1 truncate text-[10px] font-semibold text-indigo-700">
                                Viewing: {currentCompany.name}{currentCompany.impersonating ? " (as SU)" : ""}
                            </div>
                        )}
                    </div>
                    {isSU && (
                        <div className="border-b border-slate-100 bg-amber-50/60 px-3 py-2">
                            <div className="mb-1 flex items-center justify-between">
                                <div className="text-[9px] font-bold uppercase tracking-widest text-amber-800">Super User · Switch Company</div>
                                <button
                                    data-testid="goto-super-dashboard"
                                    onClick={() => { localStorage.removeItem("micm_impersonate_company"); window.location.assign("/admin/super/dashboard"); }}
                                    className="rounded bg-amber-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white hover:bg-amber-800"
                                >
                                    Super Dashboard
                                </button>
                            </div>
                            <select
                                data-testid="switch-company-select"
                                value={imp}
                                onChange={(e) => pickCompany(e.target.value)}
                                className="block w-full rounded border border-amber-300 bg-white px-2 py-1 text-xs"
                            >
                                <option value="">— pick a company —</option>
                                {companies.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name} — {c.parties_count} parties</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button
                        data-testid="change-password-btn"
                        onClick={() => { setOpen(false); onChangePassword(); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                    >
                        <Key size={14} /> Change password
                    </button>
                    <button
                        data-testid="logout-btn"
                        onClick={() => { setOpen(false); onLogout(); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50"
                    >
                        <SignOut size={14} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

function GlobalHotkeys() { useGlobalHotkeys(); return null; }

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pwOpen, setPwOpen] = useState(false);
    // v1.21.0 — poll the suspense count so the nav chip stays in sync
    const [suspenseCount, setSuspenseCount] = useState(0);
    // v1.22.0 — expose super-admin flag for conditional nav (Admin Accounts page)
    const isSuperAdmin = !!user?.is_super_admin;
    const isSuperUser = !!user?.is_super_user;
    // v1.27.91 — Data Entry Operator (DEO): staff sub-user permission that
    // hides the entire Administration tab + User Activity Log + Login History.
    const isDEO = user?.role === "staff" && user?.staff_permission === "data_entry_operator";
    // v1.25.7 — active tenant for dynamic top-left brand (JM Services vs MD Impex)
    const [activeCompany, setActiveCompany] = useState(null);
    const reloadActiveCompany = React.useCallback(() => {
        api.get("/me/company").then((r) => setActiveCompany(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        reloadActiveCompany();
    }, [location.pathname, reloadActiveCompany]);
    // v1.29.07 — Same event bridge as UserMenu: refresh the banner /
    // brand-strip / gate flags the outer layout renders (impersonation
    // banner, "SU view" chip, brand-tenant-name pill) the instant a
    // company switch fires anywhere in the app. The pathname-effect
    // above only fires on route changes, so this covers switches that
    // land back on the same path (Enter Company → /admin when already
    // on /admin).
    useEffect(() => {
        const onImp = () => reloadActiveCompany();
        window.addEventListener("micm-impersonation-changed", onImp);
        return () => window.removeEventListener("micm-impersonation-changed", onImp);
    }, [reloadActiveCompany]);

    useEffect(() => {
        let stop = false;
        const load = () => api.get("/reports/suspense/count").then((r) => { if (!stop) setSuspenseCount(r.data?.count || 0); }).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => { stop = true; clearInterval(t); };
    }, []);

    const handleLogout = async () => {
        setDrawerOpen(false);
        await logout();
        navigate("/login");
    };

    const openChangePassword = () => {
        setDrawerOpen(false);
        setPwOpen(true);
    };

    const closeDrawer = () => setDrawerOpen(false);

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    // v1.28.84 — The old SU-only redirect (SU + no impersonation + not
    // /admin/super/* → bounce to /admin/super/dashboard) was too aggressive.
    // Three symptoms:
    //  1. Firing mid-close on the mobile bottom-nav sheet left a stuck dark
    //     overlay + tap-blocking layer because the layout unmounted while
    //     the Sheet was still animating out.
    //  2. Any SU tap on a menu item that leads outside /admin/super/* was
    //     bounced back to the SU dashboard, so the mobile menu felt broken.
    //  3. Every Save on the party ledger caused a re-render; the check
    //     re-ran and threw the SU out.
    // A Super User is authenticated as an admin — they should be able to
    // view any admin route. If a page needs a tenant context, it can prompt
    // for one instead of relying on this layout-level bounce.

    return (
        <div className="flex min-h-screen flex-col bg-slate-50">
            <GlobalHotkeys />
            <ShortcutHelpFab />
            <UpdateBanner />
            <BroadcastGate />
            <PushHealthBanner />
            {/* v1.28.22 — Single sticky wrapper for the impersonation banner
                + the two-row header. Wrapping them together avoids the
                `top-0 z-30` vs `top-0 z-20` fight the two elements had
                before (banner would sit on top of the header when both
                tried to stick to the viewport). One sticky boundary now
                keeps every row visible on scroll — desktop + mobile —
                with a subtle shadow beneath for visual separation.
                The root wrapper no longer applies `overflow-x-hidden`
                because that turns the root into a scroll container in
                Chrome/Safari, silently breaking the `sticky` descendant.
                Horizontal-scroll protection has moved to `<main>` where
                it belongs. */}
            <div className="sticky top-0 z-40 shadow-sm shadow-slate-300/40">
            {isSuperUser && activeCompany?.impersonating && (
                <div
                    data-testid="impersonation-banner"
                    className="flex min-w-0 items-center justify-between gap-2 border-b border-amber-300 bg-gradient-to-r from-amber-500 to-orange-600 px-3 py-1.5 text-white sm:gap-3 sm:px-6"
                >
                    <div className="flex min-w-0 items-center gap-2 text-xs font-semibold">
                        <ShieldCheck size={14} weight="fill" className="shrink-0" />
                        <span className="hidden shrink-0 uppercase tracking-wider sm:inline">Super User view</span>
                        <span className="min-w-0 truncate opacity-90">
                            <span className="sm:hidden">Viewing <b>{activeCompany.name}</b></span>
                            <span className="hidden sm:inline">· You are managing <b>{activeCompany.name}</b> on behalf of its admin.</span>
                        </span>
                    </div>
                    <button
                        type="button"
                        data-testid="banner-exit-super"
                        onClick={() => {
                            localStorage.removeItem("micm_impersonate_company");
                            window.location.assign("/admin/super/dashboard");
                        }}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 shadow-sm hover:bg-white sm:px-3 sm:text-[11px]"
                    >
                        <span className="sm:hidden">Exit</span>
                        <span className="hidden sm:inline">Exit to Super Dashboard</span>
                    </button>
                </div>
            )}
            <header className="border-b border-slate-200 bg-white shadow-sm md:bg-white/95 md:backdrop-blur-md pt-[env(safe-area-inset-top)]">
                <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between gap-3 px-4 sm:px-6 md:h-16">
                    <div className="flex items-center gap-3">
                        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                            <SheetTrigger asChild>
                                <button
                                    data-testid="mobile-menu-btn"
                                    aria-label="Open menu"
                                    className="rounded-md border border-slate-200 bg-white p-2 text-slate-700 transition-colors hover:bg-slate-100 md:hidden"
                                >
                                    <MenuIcon size={18} weight="bold" />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-72 border-r-0 bg-slate-900 p-0 text-slate-100">
                                <MobileNav user={user} onLogout={handleLogout} onNavigate={closeDrawer} onChangePassword={openChangePassword} isSuperAdmin={isSuperAdmin} isSuperUser={isSuperUser} isDEO={isDEO} companyName={activeCompany?.name} />
                            </SheetContent>
                        </Sheet>
                        <Link to={isSuperUser && !activeCompany?.impersonating ? "/admin/super/dashboard" : "/admin"} data-testid="brand-logo" className="flex items-center gap-2.5">
                            <img
                                src="/logo512.png"
                                alt={activeCompany?.name || "MICM"}
                                className="logo-spin-glow h-10 w-10 rounded-md object-contain bg-black shadow-md ring-1 ring-amber-300/60"
                                data-testid="brand-logo-img"
                            />
                            {/* v1.28.43 — Show the shimmering wordmark on every viewport.
                                Tenant chip only shown when it adds context (SU root,
                                impersonation, or a company OTHER than the wordmark itself). */}
                            <div className="flex items-center gap-2 leading-none">
                                <Brand size="sm" shimmer />
                                {activeCompany?.name && (activeCompany.is_super_root
                                    || activeCompany.impersonating
                                    || activeCompany.name.trim().toLowerCase() !== "md impex india") && (
                                    <span data-testid="brand-tenant-name" className={`hidden sm:inline text-[10px] font-semibold uppercase tracking-wider ${activeCompany.is_super_root ? "text-amber-700" : "text-indigo-700"}`}>
                                        {activeCompany.is_super_root
                                            ? "Super User · All Tenants"
                                            : `${activeCompany.name}${activeCompany.impersonating ? " · (SU view)" : ""}`}
                                    </span>
                                )}
                            </div>
                        </Link>
                        {/* v1.28.43 — Back button visible on desktop only; mobile users
                            rely on the OS back gesture / drawer navigation. */}
                        <BackButton />
                    </div>
                    <div className="flex items-center gap-3">
                        {/* v1.25.16 — Persistent "All Tenants" button for the Super User.
                            Desktop-only header chip; on mobile the drawer already has
                            a dedicated "Super Dashboard" link so we avoid the duplicate
                            shield next to the "Exit Super View" chip. */}
                        {isSuperUser && (
                            <button
                                type="button"
                                data-testid="all-tenants-btn"
                                onClick={() => {
                                    localStorage.removeItem("micm_impersonate_company");
                                    window.location.assign("/admin/super/dashboard");
                                }}
                                title="Return to Super Dashboard (All Tenants)"
                                className="hidden sm:inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm ring-2 ring-amber-300/60 hover:bg-slate-800"
                            >
                                <ShieldCheck size={11} weight="fill" className="text-amber-300" />
                                <span className="hidden sm:inline">All Tenants</span>
                            </button>
                        )}
                        {/* v1.25.8 — Compact "Exit to Super Dashboard" chip on the header.
                            Desktop-only; the top orange "Viewing …" banner already carries
                            an EXIT button on mobile so we don't need this duplicate. */}
                        {isSuperUser && activeCompany?.impersonating && (
                            <button
                                type="button"
                                data-testid="exit-to-super-dashboard"
                                onClick={() => {
                                    localStorage.removeItem("micm_impersonate_company");
                                    window.location.assign("/admin/super/dashboard");
                                }}
                                className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm ring-2 ring-amber-300/60 hover:from-amber-600 hover:to-orange-700"
                            >
                                <ShieldCheck size={11} weight="fill" />
                                <span className="hidden sm:inline">Exit Super View</span>
                            </button>
                        )}
                        <UtrSearch isAdmin className="hidden md:block" />
                        <div className="hidden text-xs text-slate-500 lg:block">
                            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
                        </div>
                        {/* v1.28.52 — Online users + bell hidden on mobile top row
                            (they render in the second row alongside the search).
                            Prevents the wordmark from being truncated. */}
                        <div className="hidden md:inline-flex items-center gap-2">
                            <OnlineUsers />
                            <NotificationsBell viewAllHref="/admin/notifications" />
                        </div>
                        <UserMenu user={user} onLogout={handleLogout} onChangePassword={openChangePassword} />
                    </div>
                </div>
                {/* v1.28.51 — Mobile: full-width search bar + compact online/bell
                    on a second row so the top row can breathe. */}
                <div className="flex items-center gap-2 border-t border-slate-100 bg-white px-3 py-1.5 md:hidden">
                    <UtrSearch isAdmin className="flex-1 min-w-0" />
                    <OnlineUsers compact />
                    <NotificationsBell viewAllHref="/admin/notifications" compact />
                </div>
                {/* v1.25.3 — Nav row uses plain overflow-visible so dropdown
                    menus that hang below the row aren't clipped.
                    v1.25.9 — Hide the tenant-scoped nav pills whenever a Super
                    User is on their own Super Dashboard (no impersonation).
                    Those pills (Administration/Transactions/Reports/Cash In/
                    Payment) belong to a specific company workspace and make no
                    sense on the SU landing page. */}
                {!(isSuperUser && !activeCompany?.impersonating) && (
                    <div className="hidden border-t border-slate-100 bg-white md:block">
                        <div className="mx-auto flex max-w-[1600px] flex-nowrap items-center gap-1.5 overflow-visible px-4 py-1.5 sm:px-6" data-testid="top-nav">
                            <DashboardPill />
                            {NAV.filter((tab) => !(isDEO && tab.hideEntireTabForDEO)).map((tab) => (
                                <NavTab key={tab.key} tab={{ ...tab, suspenseCount, isSuperAdmin, isSuperUser, isDEO }} />
                            ))}
                            <span className="mx-0.5 h-4 w-px shrink-0 bg-slate-200" />
                            {ACTION_PILLS.map((p) => (
                                <ActionPill key={p.testId} {...p} />
                            ))}
                        </div>
                    </div>
                )}
                {/* v1.28.89 — When a Super User is on their own dashboard (no
                    company impersonation), the tenant-scoped nav pills are
                    hidden — but SU-only tools like Keyboard Shortcuts and
                    Manage Companies still make sense here. Render a slim
                    SU-only strip so they're one click away on desktop too. */}
                {(isSuperUser && !activeCompany?.impersonating) && (
                    <div className="hidden border-t border-slate-100 bg-slate-50 md:block" data-testid="su-only-nav">
                        <div className="mx-auto flex max-w-[1600px] flex-nowrap items-center gap-2 overflow-visible px-4 py-1.5 sm:px-6">
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Super User</span>
                            <NavLink to="/admin/super/companies" data-testid="su-desktop-companies" className={({isActive}) => `inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${isActive ? "bg-violet-600 text-white" : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"}`}>Manage Companies</NavLink>
                            <NavLink to="/admin/settings/shortcuts" data-testid="su-desktop-shortcuts" className={({isActive}) => `inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${isActive ? "bg-violet-600 text-white" : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"}`}>Keyboard Shortcuts</NavLink>
                            <NavLink to="/admin/settings/role-permissions" data-testid="su-desktop-role-permissions" className={({isActive}) => `inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${isActive ? "bg-violet-600 text-white" : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"}`}>Role Permissions</NavLink>
                            <NavLink to="/admin/super/backup-center" data-testid="su-desktop-backup-center" className={({isActive}) => `inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${isActive ? "bg-violet-600 text-white" : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"}`}>Backup Center</NavLink>
                            <NavLink to="/admin/super/trace-utr" data-testid="su-desktop-trace-utr" className={({isActive}) => `inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${isActive ? "bg-violet-600 text-white" : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"}`}>Trace UTR</NavLink>
                        </div>
                    </div>
                )}
            </header>
            </div>

            <main className="mx-auto w-full min-w-0 max-w-[1600px] flex-1 overflow-x-hidden px-4 py-6 pb-24 sm:px-6 sm:py-8 md:pb-8">
                <InstallPrompt />
                <Outlet />
            </main>

            <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
            <BalanceNotification />
            <QuickActionsFab />
            <IosInstallHint />
            {/* v1.28.18 — Mobile 4-tab bottom nav for Super User only. Groups
                every SU-reachable page into Home / Administration / Reports /
                Settings so a phone user can jump anywhere in ≤ 2 taps. */}
            {isSuperUser && <SuperUserBottomNav />}
        </div>
    );
}
